/**
 * Authenticated fetch wrapper with automatic token refresh.
 *
 * All Build Tracker contexts (IssuesContext, TeamContext) and App.js
 * use raw fetch() which bypasses axios interceptors. This utility
 * handles 401 responses by refreshing the access token via the
 * httpOnly refresh-token cookie, then retrying the original request.
 */

import tokenStore from './tokenStore';
import { API_URL } from '../constants/api';

let isRefreshing = false;
let refreshQueue = [];

function onTokenRefreshed(newToken) {
  refreshQueue.forEach(cb => cb(newToken));
  refreshQueue = [];
}

function addToRefreshQueue(callback) {
  refreshQueue.push(callback);
}

async function postRefresh(body) {
  const response = await fetch(`${API_URL}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include', // sends the httpOnly refresh_token cookie, when we get one
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Token refresh failed (${response.status})`);
  return response.json();
}

/**
 * Two-attempt refresh, matching what App.js already does.
 *
 * The cookie alone is not enough: it is set on foundersplus.nl while this call
 * goes cross-origin to Railway, so plenty of browsers never forward it (Safari
 * ITP blocks it outright). Attempt 2 uses the refresh token we deliberately
 * keep in localStorage for exactly that case.
 */
async function tryRefreshToken() {
  let data;
  try {
    data = await postRefresh({});
  } catch (cookieError) {
    const stored = tokenStore.getRefreshToken();
    if (!stored) throw cookieError;
    data = await postRefresh({ refresh_token: stored });
  }

  tokenStore.set(data.access_token); // updates memory + localStorage compat shim
  // The backend rotates on every refresh and revokes the previous jti. Failing
  // to persist the new one leaves a dead token in localStorage, which is what
  // silently breaks the *next* cold start.
  if (data.refresh_token) tokenStore.setRefreshToken(data.refresh_token);
  return data.access_token;
}

/**
 * Drop-in replacement for fetch() that automatically:
 * 1. Attaches the Bearer token from tokenStore
 * 2. Retries once on 401 after refreshing the access token via cookie
 * 3. Logs the user out if refresh also fails
 *
 * Usage: const response = await authenticatedFetch(url, options);
 */
export async function authenticatedFetch(url, options = {}) {
  const headers = { ...options.headers };
  const token = tokenStore.get();
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  // First attempt
  const response = await fetch(url, { ...options, headers, credentials: 'include' });

  if (response.status !== 401) {
    return response;
  }

  // 401 — attempt token refresh via cookie
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      addToRefreshQueue(async (newToken) => {
        try {
          headers.Authorization = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, { ...options, headers, credentials: 'include' });
          resolve(retryResponse);
        } catch (err) {
          reject(err);
        }
      });
    });
  }

  isRefreshing = true;

  try {
    const newToken = await tryRefreshToken();
    isRefreshing = false;
    onTokenRefreshed(newToken);
    headers.Authorization = `Bearer ${newToken}`;
    return fetch(url, { ...options, headers, credentials: 'include' });
  } catch (refreshError) {
    isRefreshing = false;
    refreshQueue = [];
    tokenStore.clear();
    // Both attempts failed, so the stored refresh token is dead too. Leaving it
    // behind makes the next load retry it, fail again, and bounce to '/' — a
    // logout loop instead of a clean login screen.
    tokenStore.clearRefreshToken();
    localStorage.removeItem('welcomeSeen');
    window.location.href = '/';
    throw refreshError;
  }
}

export default authenticatedFetch;
